/**
 * Viewer Toolbar
 *
 * Top toolbar for the unified session viewer.
 * Shows the space › room breadcrumb (session ID as fallback), connection dots, layout controls, panel visibility, and fullscreen.
 * Touch-friendly with minimum 44px tap targets on mobile.
 */

import type { ViewerBreadcrumbRoom } from '@polygraf/shared'
import {
	Columns2,
	Eye,
	EyeOff,
	FileText,
	Keyboard,
	Maximize2,
	Minimize2,
	Rows3,
	Video,
	X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ViewerBreadcrumb } from './viewer-breadcrumb'

export type LayoutMode = 'stacked' | 'side-by-side'

interface ViewerToolbarProps {
	sessionId: string
	/** Space and room the session belongs to; null while loading or for a roomless session. */
	room: ViewerBreadcrumbRoom | null
	/** false in the native WebView, where a link to /spaces/… leads nowhere. */
	breadcrumbLinked: boolean
	layout: LayoutMode
	onLayoutChange: (layout: LayoutMode) => void
	transcriptVisible: boolean
	onTranscriptVisibleChange: (visible: boolean) => void
	videoVisible: boolean
	onVideoVisibleChange: (visible: boolean) => void
	transcriptConnected: boolean
	videoConnected: boolean
	isFullscreen: boolean
	onToggleFullscreen: () => void
}

export function ViewerToolbar({
	sessionId,
	room,
	breadcrumbLinked,
	layout,
	onLayoutChange,
	transcriptVisible,
	onTranscriptVisibleChange,
	videoVisible,
	onVideoVisibleChange,
	transcriptConnected,
	videoConnected,
	isFullscreen,
	onToggleFullscreen,
}: ViewerToolbarProps) {
	const { t } = useTranslation()
	const layoutLabel =
		layout === 'stacked' ? t('viewer.toolbar.layoutSideBySide') : t('viewer.toolbar.layoutStacked')
	const fullscreenLabel = isFullscreen
		? t('viewer.toolbar.exitFullscreen')
		: t('viewer.toolbar.fullscreen')
	return (
		<div className="flex items-center justify-between px-3 py-2 sm:px-4 bg-base-100 border-b border-base-300 shrink-0">
			{/* Left: session ID + connection dots */}
			<div className="flex items-center gap-3 min-w-0">
				{room ? (
					<ViewerBreadcrumb
						room={room}
						linked={breadcrumbLinked}
						className="min-w-0 max-w-[40vw] sm:max-w-md"
						title={sessionId}
					/>
				) : (
					<span
						className="font-mono text-xs sm:text-sm text-base-content/70 truncate max-w-[120px] sm:max-w-[200px]"
						title={sessionId}
					>
						{sessionId}
					</span>
				)}
				<div className="flex items-center gap-2 shrink-0">
					<StatusDot
						icon={<FileText size={12} />}
						connected={transcriptConnected}
						label={t('viewer.toolbar.transcript')}
					/>
					<StatusDot
						icon={<Video size={12} />}
						connected={videoConnected}
						label={t('viewer.toolbar.video')}
					/>
				</div>
			</div>

			{/* Right: controls — all buttons have min 44px touch target */}
			<div className="flex items-center gap-0.5 sm:gap-1">
				{/* Panel visibility */}
				<button
					type="button"
					className={`p-2.5 sm:p-2 rounded-lg active:bg-base-300 transition-colors flex items-center gap-1 ${transcriptVisible ? 'text-base-content' : 'text-base-content/30'}`}
					onClick={() => onTranscriptVisibleChange(!transcriptVisible)}
					title={`${transcriptVisible ? t('viewer.toolbar.hideTranscript') : t('viewer.toolbar.showTranscript')} (1)`}
					aria-label={`${t('viewer.toolbar.transcript')} (T)`}
					aria-pressed={transcriptVisible}
					aria-keyshortcuts="1"
				>
					{transcriptVisible ? <Eye size={18} /> : <EyeOff size={18} />}
					<span className="text-xs hidden sm:inline">T</span>
				</button>
				<button
					type="button"
					className={`p-2.5 sm:p-2 rounded-lg active:bg-base-300 transition-colors flex items-center gap-1 ${videoVisible ? 'text-base-content' : 'text-base-content/30'}`}
					onClick={() => onVideoVisibleChange(!videoVisible)}
					title={`${videoVisible ? t('viewer.toolbar.hideVideo') : t('viewer.toolbar.showVideo')} (2)`}
					aria-label={`${t('viewer.toolbar.video')} (V)`}
					aria-pressed={videoVisible}
					aria-keyshortcuts="2"
				>
					{videoVisible ? <Eye size={18} /> : <EyeOff size={18} />}
					<span className="text-xs hidden sm:inline">V</span>
				</button>

				<div className="w-px h-5 bg-base-300 mx-1 hidden sm:block" />

				{/* Layout toggle */}
				<button
					type="button"
					className="p-2.5 sm:p-2 rounded-lg active:bg-base-300 transition-colors"
					onClick={() => onLayoutChange(layout === 'stacked' ? 'side-by-side' : 'stacked')}
					title={`${layoutLabel} (L)`}
					aria-label={layoutLabel}
					aria-keyshortcuts="L"
				>
					{layout === 'stacked' ? <Rows3 size={18} /> : <Columns2 size={18} />}
				</button>

				{/* Fullscreen */}
				<button
					type="button"
					className="p-2.5 sm:p-2 rounded-lg active:bg-base-300 transition-colors"
					onClick={onToggleFullscreen}
					title={`${fullscreenLabel} (F)`}
					aria-label={fullscreenLabel}
					aria-keyshortcuts="F"
				>
					{isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
				</button>
			</div>
		</div>
	)
}

function StatusDot({
	icon,
	connected,
	label,
}: {
	icon: React.ReactNode
	connected: boolean
	label: string
}) {
	const { t } = useTranslation()
	const text = t('viewer.toolbar.status', {
		label,
		state: connected ? t('viewer.toolbar.connected') : t('viewer.toolbar.disconnected'),
	})
	return (
		<span
			className={`flex items-center gap-1 text-xs ${connected ? 'text-green-600' : 'text-red-400'}`}
			role="img"
			title={text}
			aria-label={text}
		>
			{icon}
			<span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-400'}`} />
		</span>
	)
}

/** Keyboard shortcuts legend overlay */
export function ShortcutsLegend({ onClose }: { onClose: () => void }) {
	const { t } = useTranslation()
	return (
		<div className="absolute top-14 right-4 z-50 bg-base-100 border border-base-300 rounded-xl p-5 shadow-lg text-sm space-y-3 min-w-[250px]">
			<div className="flex items-center justify-between mb-2">
				<span className="font-medium flex items-center gap-2">
					<Keyboard size={16} />
					{t('viewer.keyboard.shortcuts')}
				</span>
				<button
					type="button"
					className="p-2 rounded-lg hover:bg-base-200 active:bg-base-300 transition-colors"
					onClick={onClose}
					aria-label={t('common.close')}
				>
					<X size={16} />
				</button>
			</div>
			<ShortcutRow shortcut="F" label={t('viewer.toolbar.shortcuts.toggleFullscreen')} />
			<ShortcutRow shortcut="L" label={t('viewer.toolbar.shortcuts.toggleLayout')} />
			<ShortcutRow shortcut="1" label={t('viewer.toolbar.shortcuts.toggleTranscript')} />
			<ShortcutRow shortcut="2" label={t('viewer.toolbar.shortcuts.toggleVideo')} />
			<ShortcutRow shortcut="ESC" label={t('viewer.toolbar.shortcuts.exitFullscreen')} />
		</div>
	)
}

function ShortcutRow({ shortcut, label }: { shortcut: string; label: string }) {
	return (
		<div className="flex items-center justify-between gap-4">
			<span className="text-base-content/60">{label}</span>
			<kbd className="bg-base-200 px-2.5 py-1 rounded text-xs font-mono min-w-[2.5rem] text-center">
				{shortcut}
			</kbd>
		</div>
	)
}
